import { Typography } from "@/components/custom/typography";
import { cn, formatDate } from "@/lib/utils";
import { NotificationContentType } from "@/types/notifications";
import Link from "next/link";
import { memo } from "react";
import { useMarkNotificationsSeen } from "@/hooks/notification-hooks/useMarkNotificationsSeen";
import { useTranslations } from "next-intl";
import { useUser } from "@/stores/user";
import { useRouter } from "next/navigation";
import { USER_TYPE } from "@/lib/constants";

const NEXT_PUBLIC_API_GATEWAY = process.env.NEXT_PUBLIC_API_GATEWAY || "";

function getSafeHref(targetUrl: string): string {
  const full = `${NEXT_PUBLIC_API_GATEWAY}${targetUrl}`;
  try {
    const parsed = new URL(full, window.location.origin);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return full;
    }
  } catch {
    // invalid URL
  }
  return "/";
}

type RedirectAction =
  | { type: "none" }
  | { type: "push"; href: string }
  | { type: "replace"; href: string };

function getRedirectAction(
  content: NotificationContentType,
  userType: string | undefined,
): RedirectAction {
  if (content.type === "Announcement") {
    if (userType === USER_TYPE.platform) return { type: "none" };
    return { type: "push", href: getSafeHref(`/dashboard/notifications/${content.id}`) };
  }

  if (content.type === "ContractAndOperationManagement") {
    if (content.target_url?.includes("tab=payment")) {
      return { type: "push", href: getSafeHref(content.target_url) };
    }
    const contractId = encodeURIComponent(content.notification_data?.notification_refer_id ?? "");
    const role = encodeURIComponent(userType ?? "");
    const notiContractStatus = encodeURIComponent(content?.notification_data?.notification_contract_status ?? "");
    return { type: "replace", href: `/contracts/go?contractId=${contractId}&src=notification&role=${role}&notiContractStatus=${notiContractStatus}` };
  }

  if (content.target_url) {
    return { type: "push", href: getSafeHref(content.target_url) };
  }

  return { type: "none" };
}

interface NotificationContentProps {
  content: NotificationContentType & { titleToShow?: string };
  titleToShow: string;
}

function NotificationContentBase({
  content,
  titleToShow,
}: NotificationContentProps) {
  const markSeen = useMarkNotificationsSeen();
  const t = useTranslations("notification");
  const { userInfo } = useUser();
  const router = useRouter();

  const handleClick = async () => {
    if (!content.read_at) {
      await markSeen
        .mutateAsync({ ids: [content.id], isMarkAll: false })
        .catch((err) => console.error("markSeen failed:", err));
    }

    const action = getRedirectAction(content, userInfo?.user?.user_type);
    if (action.type === "push") router.push(action.href);
    // Full page navigation to let browser handle the 302 redirect from route handler
    if (action.type === "replace") window.location.href = action.href;
  };

  return (
    <div
      className="flex justify-between my-1 cursor-pointer"
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* Left content */}
      <div className="flex-1 w-30 flex flex-col">
        <div className="inline-flex items-center gap-1">
          <div
            className={cn(
              "w-1 h-1 rounded-full -mt-2",
              !content.read_at ? "bg-red-500" : "bg-gray-600",
            )}
          />
          <Typography as={"span"} variant="detail1" tone="lightnormal">
            {t(`notification_type.${content.type}`)}
          </Typography>
          <Typography tone="lightsilver">|</Typography>
          <Typography
            as={"span"}
            variant="detail1"
            tone="strong"
            className="font-medium"
          >
            {titleToShow}
          </Typography>
        </div>
        <Typography as={"span"} variant="body4" className="whitespace-pre-line">
          {content.message}
        </Typography>
      </div>

      {/* Right content */}
      <div className="flex flex-col justify-between items-end">
        <Typography as={"span"} variant="detail2" tone="description">
          {formatDate(content.created_at, "second")}
        </Typography>
        <Link
          href={""}
          className={cn(
            "underline underline-offset-3 text-xs",
            !content.read_at ? "text-primary-300" : "text-gray-600",
          )}
          onClick={handleClick}
        >
          {t("content.viewDetail")}
        </Link>
      </div>
    </div>
  );
}

export const NotificationContent = memo(NotificationContentBase);
